import * as Dialog from "@radix-ui/react-dialog";
import { Clapperboard, FileText, Image, Sparkles, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

/**
 * New Creative launcher (locked decision 9; decision O3): a contextual action entry over
 * existing work. It never creates a commercial Creative Request — those come from ApexOne.
 * Actions that need workflow data are shown disabled until M1 lands (no fake behaviour).
 */
const ACTIONS = [
  { key: "open_request", icon: FileText, to: "/requests", enabled: false },
  { key: "open_strategy", icon: Sparkles, to: "/projects", enabled: false },
  { key: "create_job", icon: Clapperboard, to: "/production", enabled: false },
  { key: "upload_to_job", icon: Upload, to: "/production", enabled: false },
  { key: "open_studio", icon: Image, to: "/studio/image", enabled: true },
] as const;

export function ActionLauncher({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed left-1/2 top-[18vh] z-50 w-[560px] max-w-[92vw] -translate-x-1/2 rounded-[var(--radius-panel)] border border-line bg-surface p-6 shadow-[var(--shadow-card)]">
          <div className="flex items-start justify-between">
            <div>
              <Dialog.Title className="text-[20px] font-semibold text-ink">{t("launcher.title")}</Dialog.Title>
              <Dialog.Description className="mt-1 text-[14px] text-ink-2">
                {t("launcher.description")}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label={t("common.close")}
              className="rounded-[var(--radius-control)] p-1.5 text-ink-3 hover:bg-elevated hover:text-ink"
            >
              <X aria-hidden="true" className="size-5" />
            </Dialog.Close>
          </div>
          <ul className="mt-5 space-y-1.5">
            {ACTIONS.map((a) => (
              <li key={a.key}>
                <button
                  type="button"
                  disabled={!a.enabled}
                  onClick={() => {
                    onOpenChange(false);
                    navigate(a.to);
                  }}
                  className="flex w-full items-center gap-3 rounded-[var(--radius-card)] border border-line px-4 py-3 text-left hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-transparent"
                >
                  <a.icon aria-hidden="true" className="size-5 text-violet" />
                  <span className="flex-1 text-[15px] text-ink">{t(`launcher.${a.key}`)}</span>
                  {!a.enabled && (
                    <span className="text-[12px] text-ink-3">{t("launcher.unavailable_until_workflow")}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
